import React, { useState, useEffect } from "react"

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Slide,
    TextField,
    Box,
    Divider,
    Typography,
    Tooltip,
    Autocomplete,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Stack,
    IconButton
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close"
import SearchIcon from "@mui/icons-material/Search"
import AddIcon from "@mui/icons-material/Add"
import AddEnterpriseGroupForm from "../../Crm/TabTables/EnterpriseTable/AddEnterpriseGroupForm"
import { GroupChip } from "../../Groups/GroupBadge"

import PropTypes from "prop-types"

import { CnpjMask, TelephoneMask, PostalCodeMask, isValidCnpj, normalizeCnpj } from "../../../utils/masks"

import api from "../../../api"
import { getSession } from "../../../auth"

import useNotification from "../../Notification"
import TooltipIndicators from '../../Dash/Tooltip'

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
})

let timeoutId

const ENTERPRISE_SIZE_OPTIONS = [
    { value: "mei", label: "MEI" },
    { value: "me", label: "Microempresa" },
    { value: "epp", label: "Empresa de Pequeno Porte" },
    { value: "medium", label: "Média Empresa" },
    { value: "large", label: "Grande Empresa" }
]

const getSizeFromCnpjData = (data) => {
    if (data?.simei?.optante)
        return "mei"
    switch (data?.porte) {
        case "MICRO EMPRESA":
            return "me"
        case "EMPRESA DE PEQUENO PORTE":
            return "epp"
        default:
            return null
    }
}

const normalizeEnterpriseData = (data) => ({
    ...data,
    domain: data.domain === "" ? null : data.domain,
    size: data.size === "" ? null : data.size
})

const isValidEmail = (value = "") => {
    const email = String(value).trim()

    if (!email)
        return true

    if (!/^[^\s@]+@[^\s@]+$/.test(email))
        return false

    const [localPart, domain] = email.split("@")

    if (!localPart || localPart.startsWith(".") || localPart.endsWith(".") || localPart.includes(".."))
        return false

    if (!domain || domain.includes(".."))
        return false

    const domainLabels = domain.split(".")
    return domainLabels.length > 1 && domainLabels.every((label) => /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(label))
}

const ContactEnterpriseDialog = ({ open, handleClose, createEnterprise, updateEnterprise, selectedEnterprise, disabled=false, customFields=[] }) => {
    const session = getSession()
    const isAdminOrSupervisor = (session.groups || []).some(each => each.name === "administrador" || each.name === "supervisor")
    const canCreateSegments = isAdminOrSupervisor || session.profile.enterprise.settings.agent_can_create_segments

    const [state, setState] = useState({
        id                : null,
        name              : "",
        socialReason      : "",
        telephone         : "",
        email             : "",
        cnpj              : "",
        address           : "",
        addressNumber     : "",
        addressExtra      : "",
        neighborhood      : "",
        city              : "",
        estate            : "",
        postalCode        : "",
        comments          : "",
        domain            : "",
        groupsSelectOpen  : false,
        contactGroups     : [],
        groupsLoading     : false,
        selectedGroups    : [],
        nextGroupsPointer : null,
        customFieldsData  : {},
        code              : "",
        field             : "",
        linkedinUrl      : "",
        size              : ""
    })
    const [showNewSegmentModal, setShowNewSegmentModal] = useState(false)
    const cnpjInvalid = Boolean(state.cnpj) && !isValidCnpj(state.cnpj)

    const submit = () => {
        if (!isValidEmail(state.email)) {
            useNotification(
                "Atenção!",
                "Informe um e-mail válido.",
                "warning"
            )
            return
        }

        if (cnpjInvalid) {
            useNotification(
                "Atenção!",
                "Informe um CNPJ válido.",
                "warning"
            )
            return
        }

        const normalizedState = normalizeEnterpriseData(state)
        return (selectedEnterprise !== null && selectedEnterprise !== undefined) ? updateEnterprise(normalizedState) : createEnterprise(normalizedState)
    }

    useEffect(() => {
        const loadCustomFieldValues = async () => {
            if (selectedEnterprise !== null && selectedEnterprise !== undefined) {
                const response = await api.get(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${selectedEnterprise.id}/custom_fields/`)
                const customFieldsData = {}
                if (response.status === 200) {
                    response.data.forEach(field => {
                        customFieldsData[field.custom_field] = field.value
                    })
                }
                
                setState((prevState) => {
                    const updatedState = Object.keys(selectedEnterprise).reduce((acc, key) => {
                        const parsedKey = key.replace(/_([a-z])/g, (match, group1) => group1.toUpperCase())
                        if (prevState.hasOwnProperty(parsedKey)) 
                            acc[parsedKey] = selectedEnterprise[key] || ""
                        return acc
                    }, {})

                    return { ...prevState, ...updatedState, selectedGroups: selectedEnterprise.groups, customFieldsData }
                })
            }
        }
        loadCustomFieldValues()
    }, [selectedEnterprise])

    const getGroups = async (search, groupsUrl) => {
        if (groupsUrl === undefined || (typeof search !== 'object' && search !== undefined)) {
            setState((prevState) => ({
                ...prevState,
                groupsSelectOpen: true,
                groupsloading: true,
                contactGroups: []
            }))
        } else {
            setState((prevState) => ({
                ...prevState,
                groupsSelectOpen: true,
                groupsloading: true
            }))
        }

        const enterpriseId = getSession().profile.enterprise.id

        let url
        if (groupsUrl !== undefined)
            url = `/api/${groupsUrl.split('/api/')[1]}`
        else {
            url = `/api/enterprises/${enterpriseId}/contact_group/noserialize/`
            if (typeof search !== 'object' && search !== undefined)
                url = `${url}?search=${search}`
        }

        setState((prevState) => ({...prevState, groupsLoading: true }))
        const response = await api.get(url)
        setState((prevState) => ({...prevState, groupsLoading: false }))
        
        if (response.status === 200)
            setState((prevState) => ({
                ...prevState, 
                groupsSelectOpen: true,
                nextGroupsPointer: response.data.next,
                contactGroups: [...prevState.contactGroups, ...response.data.results.filter(newItem => 
                    ! prevState.contactGroups.some(existing => existing.id === newItem.id)
                )]
            }))
    }

    const canRemoveContactGroupRelation = async (group) => {
        const enterpriseId = getSession().profile.enterprise.id
        const response = await api.post(`/api/enterprises/${enterpriseId}/contact_group/can_remove_relation/`, {
            group_id: group.id
        })
        return response.status === 200
    }

    const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = event.target;
        if (state.nextGroupsPointer && scrollHeight - scrollTop <= clientHeight + 5)
            getGroups(undefined, state.nextGroupsPointer)
    }

    const fillEnterpriseData = async () => {
        const cnpj = normalizeCnpj(state.cnpj)

        if (!isValidCnpj(cnpj)) {
            useNotification(
                "Atenção",
                "CNPJ inválido ou incompleto.",
                "warning"
            )
            return
        }

        const response = await api.get(`api/cnpj/get_data/${cnpj}/`)
        if (response.status === 200) {
            setState((prev) => ({
                ...prev,
                name          : response.data?.fantasia || (response.data?.nome ?? ""),
                socialReason  : response.data?.nome ?? "",
                telephone     : response.data?.telefone ?? "",
                email         : response.data?.email ?? "",
                address       : response.data?.logradouro ?? "",
                addressNumber : response.data?.numero ?? "",
                addressExtra  : response.data?.complemento ?? "",
                neighborhood  : response.data?.bairro ?? "",
                city          : response.data?.municipio ?? "",
                estate        : response.data?.uf ?? "",
                postalCode    : response.data?.cep ?? "",
                code          : response.data?.atividade_principal?.[0]?.code ?? "",
                field         : response.data?.atividade_principal?.[0]?.text ?? "",
                size          : getSizeFromCnpjData(response.data) ?? prev.size
            }))
        } else if ([400, 404].includes(response.status)) {
            switch (response.data.message) {
                case "CNPJ inválido":
                    useNotification(
                        "Atenção", 
                        "CNPJ inválido ou incompleto.", 
                        "warning"
                    )
                    break
                case "not in cache":
                    useNotification(
                        "Atenção", 
                        "Não foi encontrada nenhuma empresa relacionada a este CNPJ.", 
                        "warning"
                    )
                    break
                default:
                    useNotification(
                        "Atenção", 
                        "Não encontramos nenhum informação para este CNPJ.", 
                        "warning"
                    )
                    break
            }
        } else if (response.status === 429) {
            useNotification(
                "Atenção",
                "Limite de solicitações atingido. Tente novamente em alguns instantes.",
                "warning"
            )
        } else {
            useNotification(
                "Ops!", 
                "Algo deu errado, tente novamente em alguns instantes.", 
                "danger"
            )
        }
    }

    const subsObj = (list, item) => {
        if (list.some(existing => existing.id === item.id))
            return list
        return [item, ...list]
    }

    const handleUpdateGroups = (updater) => {
        setState((prevState) => {
            const previousGroups = prevState.contactGroups || []
            const updatedGroups = updater(previousGroups)
            let newGroup = null
            if (updatedGroups.length > previousGroups.length)
                newGroup = updatedGroups.find(group => !previousGroups.some(prev => prev.id === group.id))
            const selectedGroups = newGroup
                ? [...(prevState.selectedGroups || []), newGroup]
                : prevState.selectedGroups
            return {
                ...prevState,
                contactGroups: updatedGroups,
                selectedGroups
            }
        })
    }

    return (
        <Dialog
            open={open}
            keepMounted
            onClose={()=>{}}
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
            // disablePortal
            // disableEnforceFocus
            fullWidth
            maxWidth="sm"
            sx={{ position: "absolute" }}
        >
            <DialogTitle id="alert-dialog-slide-title" className={'group-flexbox'}>
                {selectedEnterprise !== null ? "Empresa" : "Nova Empresa"}
                <Tooltip title="Fechar">
                    <Button className="action-button">
                        <CloseIcon onClick={handleClose} />
                    </Button>
                </Tooltip>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <DialogContentText id="alert-dialog-slide-description">
                    <Typography marginBottom="1rem" fontSize="19px" fontWeight="500" color="#21446c">
                        Dados Cadastrais
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" className="push-bottom">
                        <TextField
                            size="small"
                            label="CNPJ"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.cnpj}
                            onChange={(event) => setState((prevState) => ({ ...prevState, cnpj: event.target.value }))}
                            InputProps={{ inputComponent: CnpjMask }}
                            disabled={disabled}
                        />
                        <Button
                            variant="contained"
                            onClick={fillEnterpriseData}
                            disabled={disabled}
                            sx={{ whiteSpace: "nowrap" }}
                        >
                            Buscar
                        </Button>
                    </Stack>
                    <TextField
                        size="small"
                        label="Nome"
                        fullWidth
                        type="text"
                        variant="outlined"
                        value={state.name}
                        onChange={(event) => setState((prevState) => ({ ...prevState, name: event.target.value }))}
                        className="push-bottom"
                        disabled={disabled}
                    />
                    <TextField
                        size="small"
                        label="Razão Social"
                        fullWidth
                        type="text"
                        variant="outlined"
                        value={state.socialReason}
                        onChange={(event) => setState((prevState) => ({ ...prevState, socialReason: event.target.value }))}
                        className="push-bottom"
                        disabled={disabled}
                    />
                    <FormControl fullWidth size="small" className="push-bottom">
                        <InputLabel>Porte</InputLabel>
                        <Select
                            value={state.size}
                            label="Porte"
                            onChange={(event) => setState((prevState) => ({ ...prevState, size: event.target.value }))}
                            disabled={disabled}
                        >
                            <MenuItem value="">
                                <em>Não informado</em>
                            </MenuItem>
                            {ENTERPRISE_SIZE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
                        <TextField
                            size="small"
                            label="Telefone"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.telephone}
                            onChange={(event) => setState((prevState) => ({ ...prevState, telephone: event.target.value }))}
                            className="push-bottom"
                            InputProps={{ inputComponent: TelephoneMask }}
                            disabled={disabled}
                        />
                        <TextField
                            size="small"
                            label="E-mail"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.email}
                            onChange={(event) => setState((prevState) => ({ ...prevState, email: event.target.value }))}
                            error={!isValidEmail(state.email)}
                            helperText={!isValidEmail(state.email) ? "Informe um e-mail válido." : " "}
                            className="push-bottom"
                            disabled={disabled}
                        />
                    </Box>
                    <TextField
                        size="small"
                        label="LinkedIn"
                        fullWidth
                        type="text"
                        variant="outlined"
                        value={state.linkedinUrl}
                        onChange={(event) => setState((prevState) => ({ ...prevState, linkedinUrl: event.target.value }))}
                        className="push-bottom"                        
                        disabled={disabled}
                    />
                    <TextField
                        size="small"
                        label={<>
                        Domínio 
                            <span style={{marginLeft: '.5rem'}}>
                            <TooltipIndicators
                                text={
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <Box>Todos os novos contatos cadastrados cujo e‑mail pertença ao domínio serão alocados a essa empresa.
                                            Formato: exemplo.com.br
                                            Não utilizar o @
                                        </Box>
                                    </Box>
                                }
                                margin="0px"
                            />
                            </span>
                        </>}
                        fullWidth
                        type="text"
                        variant="outlined"
                        value={state.domain}
                        onChange={(event) => setState((prevState) => ({ ...prevState, domain: event.target.value }))}
                        className="push-bottom"                        
                        disabled={disabled}
                    />
                    <Typography marginBottom="1rem" fontSize="19px" fontWeight="500" color="#21446c">
                        Área de Atuação
                    </Typography>
                    <Box style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
                        <TextField
                            sx={{ flex: 1 }}
                            size="small"
                            label="Código"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.code}
                            onChange={(event) => setState((prevState) => ({ ...prevState, code: event.target.value }))}
                            className="push-bottom"
                            disabled={disabled}
                        />
                        <TextField
                            sx={{ flex: 3 }}
                            size="small"
                            label="Área"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.field}
                            onChange={(event) => setState((prevState) => ({ ...prevState, field: event.target.value }))}
                            className="push-bottom"
                            disabled={disabled}
                        />
                    </Box>
                    <Typography marginBottom="1rem" fontSize="19px" fontWeight="500" color="#21446c">
                        Endereço
                    </Typography>
                    <TextField
                        size="small"
                        label="Endereço"
                        fullWidth
                        type="text"
                        variant="outlined"
                        value={state.address}
                        onChange={(event) => setState((prevState) => ({ ...prevState, address: event.target.value }))}
                        className="push-bottom"
                        disabled={disabled}
                    />
                    <Box style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
                        <TextField
                            size="small"
                            label="Número"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.addressNumber}
                            onChange={(event) => setState((prevState) => ({ ...prevState, addressNumber: event.target.value }))}
                            className="push-bottom"
                            disabled={disabled}
                        />
                        <TextField
                            size="small"
                            label="Complemento"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.addressExtra}
                            onChange={(event) => setState((prevState) => ({ ...prevState, addressExtra: event.target.value }))}
                            className="push-bottom"
                            disabled={disabled}
                        />
                        <TextField
                            size="small"
                            label="Bairro"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.neighborhood}
                            onChange={(event) => setState((prevState) => ({ ...prevState, neighborhood: event.target.value }))}
                            className="push-bottom"
                            disabled={disabled}
                        />
                    </Box>
                    <Box style={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
                        <TextField
                            size="small"
                            label="Cidade"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.city}
                            onChange={(event) => setState((prevState) => ({ ...prevState, city: event.target.value }))}
                            className="push-bottom"
                            disabled={disabled}
                        />
                        <TextField
                            size="small"
                            label="Estado"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.estate}
                            onChange={(event) => setState((prevState) => ({ ...prevState, estate: event.target.value }))}
                            className="push-bottom"
                            disabled={disabled}
                        />
                        <TextField
                            size="small"
                            label="CEP"
                            fullWidth
                            type="text"
                            variant="outlined"
                            value={state.postalCode}
                            onChange={(event) => setState((prevState) => ({ ...prevState, postalCode: event.target.value }))}
                            className="push-bottom"
                            InputProps={{ inputComponent: PostalCodeMask }}
                            disabled={disabled}
                        />
                    </Box>
                    <TextField
                        size="small"
                        label="Comentários"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={state.comments}
                        onChange={(event) => setState((prevState) => ({ ...prevState, comments: event.target.value }))}
                        className="push-bottom"
                        disabled={disabled}
                    />
                    
                    {customFields && customFields.length > 0 && (
                        <>
                            <Typography marginTop="1rem" marginBottom="1rem" fontSize="19px" fontWeight="500" color="#21446c">
                                Campos Customizados
                            </Typography>
                            {customFields.map((field) => {
                                const fieldValue = (state.customFieldsData && state.customFieldsData[field.id]) || ''
                                
                                if (field.field_type === 'boolean') {
                                    return (
                                        <FormControlLabel
                                            key={field.id}
                                            control={
                                                <Checkbox
                                                    checked={fieldValue === 'true' || fieldValue === true}
                                                    onChange={(e) => setState((prevState) => ({
                                                        ...prevState,
                                                        customFieldsData: {
                                                            ...prevState.customFieldsData,
                                                            [field.id]: e.target.checked
                                                        }
                                                    }))}
                                                    disabled={disabled}
                                                />
                                            }
                                            label={field.field_name}
                                            className="push-bottom"
                                        />
                                    )
                                } else if (field.field_type === 'select') {
                                    return (
                                        <FormControl key={field.id} fullWidth size="small" className="push-bottom">
                                            <InputLabel>{field.field_name}</InputLabel>
                                            <Select
                                                value={fieldValue}
                                                label={field.field_name}
                                                onChange={(e) => setState((prevState) => ({
                                                    ...prevState,
                                                    customFieldsData: {
                                                        ...prevState.customFieldsData,
                                                        [field.id]: e.target.value
                                                    }
                                                }))}
                                                disabled={disabled}
                                            >
                                                <MenuItem value="">
                                                    <em>Nenhum</em>
                                                </MenuItem>
                                                {field.options?.map((option) => (
                                                    <MenuItem key={option.id} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    )
                                } else if (field.field_type === 'textarea') {
                                    return (
                                        <TextField
                                            key={field.id}
                                            size="small"
                                            label={field.field_name}
                                            fullWidth
                                            multiline
                                            rows={4}
                                            variant="outlined"
                                            value={fieldValue}
                                            onChange={(e) => setState((prevState) => ({
                                                ...prevState,
                                                customFieldsData: {
                                                    ...prevState.customFieldsData,
                                                    [field.id]: e.target.value
                                                }
                                            }))}
                                            className="push-bottom"
                                            disabled={disabled}
                                        />
                                    )
                                } else if (field.field_type === 'date') {
                                    return (
                                        <TextField
                                            key={field.id}
                                            size="small"
                                            label={field.field_name}
                                            fullWidth
                                            type="date"
                                            variant="outlined"
                                            value={fieldValue}
                                            onChange={(e) => setState((prevState) => ({
                                                ...prevState,
                                                customFieldsData: {
                                                    ...prevState.customFieldsData,
                                                    [field.id]: e.target.value
                                                }
                                            }))}
                                            className="push-bottom"
                                            InputLabelProps={{ shrink: true }}
                                            disabled={disabled}
                                        />
                                    )
                                } else {
                                    return (
                                        <TextField
                                            key={field.id}
                                            size="small"
                                            label={field.field_name}
                                            fullWidth
                                            type={field.field_type === 'integer' ? 'number' : 'text'}
                                            variant="outlined"
                                            value={fieldValue}
                                            onChange={(e) => setState((prevState) => ({
                                                ...prevState,
                                                customFieldsData: {
                                                    ...prevState.customFieldsData,
                                                    [field.id]: e.target.value
                                                }
                                            }))}
                                            className="push-bottom"
                                            disabled={disabled}
                                        />
                                    )
                                }
                            })}
                        </>
                    )}
                    
                    {! disabled &&
                        <>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography marginBottom="1rem" fontSize="19px" fontWeight="500" color="#21446c">
                                    Segmentos
                                </Typography>
                                {canCreateSegments && (
                                    <IconButton
                                        size="small"
                                        onClick={() => setShowNewSegmentModal(true)}
                                        aria-label="Criar segmento"
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                            <Autocomplete
                                multiple
                                sx={{ marginTop: "1rem", marginBottom: "1rem" }}
                                size="small"
                                open={state.groupsSelectOpen}
                                onOpen={getGroups}
                                onClose={() => setState((prevState) => ({ ...prevState, groupsSelectOpen: false }))}
                                isOptionEqualToValue={(option, value) => option.name === value.name}
                                getOptionLabel={(option) => option.name || ""}
                                options={state.contactGroups || []}
                                loading={state.groupsLoading}
                                filterOptions={(x) => x}
                                onInputChange={(event) => {
                                    if (timeoutId)
                                        clearTimeout(timeoutId)
                                    timeoutId = setTimeout(() => getGroups(event.target.value), 1000)
                                }}
                                value={state.selectedGroups || []}
                                onChange={async (event, newValue) => {
                                    const removedItem = state.selectedGroups?.find((item) => !newValue.some((newItem) => newItem.name === item.name))
                                    if (removedItem) {
                                        const canRemove = await canRemoveContactGroupRelation(removedItem)
                                        if (canRemove)
                                            setState((prevState) => ({ ...prevState, selectedGroups: newValue }))
                                        else
                                            useNotification(
                                                "Atenção!", 
                                                "Você não tem permissão para remover este segmento.", 
                                                "warning"
                                            )
                                    } else {
                                        setState((prevState) => ({ ...prevState, selectedGroups: newValue }))
                                    }
                                }}
                                ListboxProps={{ onScroll: handleScroll }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <GroupChip
                                            {...getTagProps({ index })}
                                            label={option.name}
                                            isEnterprise={true}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Segmentos"
                                        slotProps={{
                                            input: {
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {state.groupsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                            />
                            {showNewSegmentModal && (
                                <AddEnterpriseGroupForm
                                    open={showNewSegmentModal}
                                    handleClose={() => setShowNewSegmentModal(false)}
                                    submit={() => Promise.resolve()}
                                    enterpriseId={session.profile.enterprise.id}
                                    selectionIds={[]}
                                    updateReady={() => {}}
                                    updateGroups={handleUpdateGroups}
                                    updateEnterprises={() => {}}
                                    subsObj={subsObj}
                                    updateForceReload={() => {}}
                                    optionalTitle="Criar novo segmento de empresas"
                                />
                            )}
                        </>
                    }
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {! disabled &&
                    <>
                        <Button onClick={handleClose} color="primary">
                            Cancelar
                        </Button>
                        <Button onClick={submit} color="primary" className="push-right" disabled={! state.name || !isValidEmail(state.email)}>
                            Enviar
                        </Button>
                    </>
                }
            </DialogActions>
        </Dialog>
    )
}

ContactEnterpriseDialog.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func
};

export default ContactEnterpriseDialog
